import { CcfBox } from '@nice-ccf/ui-controls';
import styles from './ccf-consumer-card-notes-tab.module.scss';
import { useState } from 'react';
import CcfNotesTabListView from './ccf-notes-tab-list-view/ccf-notes-tab-list-view';
import CcfNotesTabHeader from './ccf-notes-tab-header/ccf-notes-tab-header';
import {
  activeContactSelector,
  updateActiveContact,
} from '../../../ccf-inbox/ccf-inbox.slice';
import { useDispatch, useSelector } from 'react-redux';
import { notesHistoryListData } from './mock-data/notes-history-mock-data';
import {
  differenceInDays,
  isToday,
  isYesterday,
  parseISO,
  subDays,
  isWithinInterval,
} from 'date-fns';
import { useMsal } from '@azure/msal-react';

/* eslint-disable-next-line */
export interface CcfConsumerCardNotesTabProps {}

/**@example */
export function CcfConsumerCardNotesTab(props: CcfConsumerCardNotesTabProps) {
  const [updatedNote, setUpdatedNote] = useState();
  const emptyNote = { title: '', content: '' };
  const [newNote, setNewNote] = useState(emptyNote);
  const [noteAnchorEl, setNoteAnchorEl] = useState<HTMLElement | null>(null);
  const openNewNote = Boolean(noteAnchorEl);

  const dispatch = useDispatch();
  const activeContact = useSelector(activeContactSelector);
  const notesData: any = activeContact?.notes;
  const [notesHistoryData, setNotesHistoryData]: any = useState(notesData);
  const [selectedFilterOption, setSelectedFilterOption] = useState('All');
  const { accounts } = useMsal();
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const loginCredentialsArray = loginCredentialsFromSSO?.split(' ');
  const assignName = `${
    loginCredentialsArray[1].charAt(0).toUpperCase() +
    loginCredentialsArray[1].slice(1).toLowerCase()
  }`;

  /**
   * Function to select interaction view
   * @example interactionViewSelectHandler()
   */
  const notesViewSelectHandler = (note?: any) => {
    const updatedNote = notesHistoryData.map((item: any) =>
      item.notesId === note.notesId
        ? { ...item, expandedMaxContent: !note.expandedMaxContent }
        : { ...item, expandedMaxContent: false }
    );
    dispatch(
      updateActiveContact({
        id: activeContact?.id || '',
        updates: { notes: updatedNote },
      })
    );
    setNotesHistoryData(updatedNote);
    setUpdatedNote(note?.noteContent || {});
  };

  /**@example */
  const editNote = (note: any, value: any) => {
    const editedNote = notesHistoryData.map((item: any) =>
      item.notesId === note.notesId
        ? { ...item, noteContent: value, agentName: `Agent ${assignName}` }
        : item
    );
    setUpdatedNote(value);
    dispatch(
      updateActiveContact({
        id: activeContact?.id || '',
        updates: { notes: editedNote },
      })
    );
    setNotesHistoryData(editedNote);
  };

  /**
   * Function to select interaction view
   * @example interactionViewSelectHandler()
   */
  const searchHandler = (searchQuery: string) => {
    if (searchQuery === '') {
      setNotesHistoryData(notesData);
    } else {
      const filteredData = notesHistoryData.filter((row: any) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchQuery)
        )
      );

      setNotesHistoryData(filteredData);
    }
  };

  /**
   * Function to open the popover
   * @example handlePopoverOpen()
   */
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNoteAnchorEl(event.currentTarget);
  };
  /**
   * Function to close the popover
   * @example handlePopoverClose()
   */
  const handlePopoverClose = () => {
    setNoteAnchorEl(null);
  };

  /**@example */
  const formatTime = (date: any) => {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesFormatted = minutes < 10 ? '0' + minutes : minutes;
    return `${hours}:${minutesFormatted}${ampm}`;
  };

  /**@example */
  const addNewNote = () => {
    if (
      newNote &&
      Object.values(newNote).some(
        (value) => value !== '' || value !== undefined
      )
    ) {
      const today = new Date().toISOString();
      const newNoteAdd = [...notesHistoryData];
      const obj = {
        ...notesHistoryData[0],
        notesId: parseInt(notesHistoryData.length),
        subTitle: newNote.content,
        noteContent: `The customer seems to be unhappy with the luggage situation.We have had multiple conversations about this but he doesn't want to make any concessions. I have elevated it to a supervisor and we will see how he responds.`,
        expandedMaxContent: false,
        noteTitle: newNote.title,
        date: today,
        agentName: `Agent ${assignName}`,
        time: formatTime(new Date()),
      };
      newNoteAdd.unshift(obj);
      dispatch(
        updateActiveContact({
          id: activeContact?.id || '',
          updates: { notes: newNoteAdd },
        })
      );
      setNotesHistoryData(newNoteAdd);
      setNewNote(emptyNote);
      setNoteAnchorEl(null);
    }
  };

  /**@example */
  const filterData = (data: any, filterOption: any) => {
    const now = new Date();
    const filteredData = data.filter((item: any) => {
      const itemDate = parseISO(item.date);

      switch (filterOption) {
        case 'today':
          return isToday(itemDate);
        case 'yesterday':
          return isYesterday(itemDate);
        case 'lastWeek':
          return isWithinInterval(itemDate, {
            start: subDays(now, 7),
            end: now,
          });
        case 'lastMonth':
          return isWithinInterval(itemDate, {
            start: subDays(now, 30),
            end: now,
          });
        case 'last90Days':
          return isWithinInterval(itemDate, {
            start: subDays(now, 90),
            end: now,
          });
        default: // 'All'
          return true;
      }
    });

    return filteredData;
  };

  /**@example */
  const handleFilter = (option: any) => {
    setSelectedFilterOption(option);
    const updatedData = filterData(notesData, option);
    setNotesHistoryData(updatedData);
    // dispatch(
    //   updateActiveContact({
    //     id: activeContact?.id || '',
    //     updates: { notes: updatedData },
    //   })
    // );
  };
  return (
    <CcfBox style={{ height: '100%' }}>
      <CcfNotesTabHeader
        notesViewSelectHandler={notesViewSelectHandler}
        searchHandler={searchHandler}
        handlePopoverOpen={handlePopoverOpen}
        handlePopoverClose={handlePopoverClose}
        handleFilter={handleFilter}
        selectedFilterOption={selectedFilterOption}
      />
      <CcfBox className={styles.notesHistory}>
        <CcfNotesTabListView
          notesViewSelectHandler={notesViewSelectHandler}
          notesHistoryData={notesHistoryData}
          editNote={editNote}
          updatedNote={updatedNote}
          setUpdatedNote={setUpdatedNote}
          newNote={newNote}
          setNewNote={setNewNote}
          handlePopoverClose={handlePopoverClose}
          noteAnchorEl={noteAnchorEl}
          openNewNote={openNewNote}
          addNewNote={addNewNote}
        />
      </CcfBox>
    </CcfBox>
  );
}

export default CcfConsumerCardNotesTab;
